import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageManagerService } from '../../../../shared/src/lib/services/local-storage-manager.service';
import { catchError, map, Observable, throwError } from 'rxjs';
import { AbilityService, AnalyticsEvent, AnalyticsService, UserModule } from '@zayan-erp/shared';
import { User, UserModuleRole, UserRole } from '@zayan-erp/administration';
import { RealtimeConnection } from '@zayan-erp/realtime';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../apps/cinari-erp-client/src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserAuthenticationService {
  private rememberMe: boolean = false;

  UserSelectedModule: UserModule | undefined;
  UserModule: UserModule[] | undefined;
  private _isAuth: boolean = true;

  constructor(
    private _router: Router,
    private _toaster: ToastrService,
    private _localStorageManagerService: LocalStorageManagerService,
    private _httpClient: HttpClient,
    private _abilityService: AbilityService,
    private _analyticsService: AnalyticsService,
    // Resolved lazily via the injector, not as a constructor param: RealtimeConnection's token
    // provider is factory-injected with this very service, so eagerly injecting it here would
    // be a circular dependency (NG0200).
    private _injector: Injector
  ) {}

  saveUser(data: any): void {
    this._localStorageManagerService.saveUserToStorage(data);
  }

  saveAdminUser(): void {
    const user = this.getUser();
    // check if user has super admin role assigned in any modules

    const hasSuperAdminRole = user.userModuleRoles.some((moduleRole: UserModuleRole) =>
      moduleRole.role.roleName.toLowerCase().includes('superadmin')
    );

    if (hasSuperAdminRole) this._localStorageManagerService.saveAdminUserToStorage();
  }

  updateUser(data: any): void {
    this._localStorageManagerService.updateUserToStorage(data);
  }

  removeOtpEmail(): void {
    this._localStorageManagerService.removeOtpEmail();
  }

  setOtpEmail(email: string): void {
    this._localStorageManagerService.setOtpEmailToStorage(email);
  }

  getOtpEmail(): string {
    return this._localStorageManagerService.getOtpEmailToStorage();
  }

  setToken(authToken: string, refreshToken: string): void {
    this._localStorageManagerService.setTokenToStorage(authToken, refreshToken);
  }

  getToken(): string {
    return this._localStorageManagerService.getTokenFromStorage();
  }

  getRefreshToken(): string {
    return this._localStorageManagerService.getRefreshTokenFromStorage();
  }

  refreshToken(): Observable<boolean> {
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getToken();
    const deviceId = this._localStorageManagerService.getOrCreateDeviceId();

    if (!refreshToken || refreshToken === '{}') {
      return throwError(() => new Error('No refresh token available'));
    }

    return this._httpClient
      .post<any>(
        `${environment.baseUrl}token/refresh`,
        {
          accessToken: accessToken,
          refreshToken: refreshToken,
          deviceId: deviceId
        },
        { withCredentials: true }
      )
      .pipe(
        map(response => {
          if (response && response.success && response.data) {
            const userInfo: User = response?.data?.userInfo;
            this.onUserLoginSuccess(
              userInfo,
              response.data.tokenDtoToReturn.accessToken,
              response.data.tokenDtoToReturn.refreshToken,
              false,
              false
            );
            return true;
          }
          return false;
        }),
        catchError(error => {
          // If refresh token fails, log out the user
          this.logout(true, true);
          return throwError(() => error);
        })
      );
  }

  isLogin(): boolean {
    return this._localStorageManagerService.isLoginValidationFromStorage();
  }

  isAuth(): boolean {
    return this._isAuth;
  }

  setisAuth(value: boolean): void {
    this._isAuth = value;
  }

  isOtpEmailSet(): boolean {
    return this._localStorageManagerService.isOtpEmailValidationFromStorage();
  }

  isAdmin(): boolean {
    return this._localStorageManagerService.isAdminValidationFromStorage();
  }

  getUser(): User {
    return this._localStorageManagerService.getUserFromStorage() as User;
  }

  getUserId(): string | null {
    return this._localStorageManagerService.getUserIdFromStorage();
  }

  getUserRole(): string {
    return this._localStorageManagerService.getUserRoleFromStorage();
  }

  logout(autoRedirect: boolean = true, captureReturnUrl: boolean = false): void {
    // Fire and forget: an already-open realtime socket is otherwise left running on the old
    // token until the server notices it expired, which can be up to an hour away.
    void this._injector.get(RealtimeConnection).stop();

    const userId = this.getUserId() ?? undefined;
    this._analyticsService.capture(AnalyticsEvent.Logout, { user_id: userId });
    this._analyticsService.reset();
    const currentUrl = this._router.url;
    const shouldCapture = captureReturnUrl && !!currentUrl && currentUrl !== '/' && !currentUrl.startsWith('/?');
    const redirectTarget = shouldCapture ? `/?returnUrl=${encodeURIComponent(currentUrl)}` : '/';

    // Try to invalidate the refresh token on the backend before clearing storage
    const refreshToken = this.getRefreshToken();
    const deviceId = this._localStorageManagerService.getOrCreateDeviceId();

    if (refreshToken && refreshToken !== '{}') {
      // Fire and forget - don't wait for response
      this._httpClient
        .post(
          `${environment.baseUrl}token/revoke`,
          {
            accessToken: this.getToken(),
            refreshToken: refreshToken,
            deviceId: deviceId
          },
          { withCredentials: true }
        )
        .subscribe({
          next: () => {
            this._localStorageManagerService.clearLocalStorage();
            this._router.navigateByUrl(redirectTarget);
          },
          error: () => {
            this._localStorageManagerService.clearLocalStorage();
            this._router.navigateByUrl(redirectTarget);
          },
          complete: () => {
            // Always clear local storage regardless of backend response
            this._localStorageManagerService.clearLocalStorage();
            this._router.navigateByUrl(redirectTarget);
          }
        });
    } else {
      // No refresh token, just clear storage
      this._localStorageManagerService.clearLocalStorage();
      if (autoRedirect) this._router.navigateByUrl(redirectTarget);
    }
  }

  setUserRole(role: UserRole): void {
    const user = this.getUser();
    user.role = role.roleName;
    user.roleDisplayName = role.displayName;
    this.saveUser(user);
    this._abilityService.updateAbilityForModule(this.UserSelectedModule?.label ?? '', role.roleName);
  }

  setUserDefaultRole(): User {
    const user = this.getUser();
    user.role = user.userModuleRoles.length > 0 ? user.userModuleRoles[0].role.roleName : '';
    user.roleDisplayName = user.userModuleRoles.length > 0 ? user.userModuleRoles[0].role.displayName : '';
    this.saveUser(user);
    return user;
  }

  setRememberMe(value: boolean): void {
    this.rememberMe = value;
  }

  getRememberMe(): boolean {
    return this.rememberMe;
  }

  onUserLoginSuccess(
    user: User,
    accessToken: string,
    refreshToken: string,
    redirectToWelcome: boolean = true,
    showToaster: boolean = true
  ): void {
    this.saveUser(user);
    this.setToken(accessToken, refreshToken);
    this.setUserDefaultRole();
    this.saveAdminUser();

    // Mirrors the stop() in logout(). Until this, the socket only opened on a reload of an
    // existing session — a fresh login stayed offline, so nothing published to that user
    // arrived until they happened to refresh. Fire and forget: no redirect waits on a socket.
    // Called after setToken so there is a token for the connection to negotiate with.
    void this._injector.get(RealtimeConnection).start();

    this.identifyUserInAnalytics(user);
    this._analyticsService.capture(AnalyticsEvent.LoginSucceeded, {
      user_id: user.id,
      email: user.email
    });

    if (redirectToWelcome) {
      const returnUrl = this.getValidatedReturnUrl();
      this._router.navigateByUrl(returnUrl ?? '/welcome');
    }

    const lastName = user?.lastName;
    const capitalizedLastName = lastName ? lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase() : '';
    if (showToaster) this._toaster.success(`Hello ${capitalizedLastName}, Welcome back`, 'Success!');
  }

  private getValidatedReturnUrl(): string | null {
    try {
      // Works for both direct login (/?returnUrl=...) and OTP (/otp-verification?returnUrl=...).
      const urlTree = this._router.parseUrl(this._router.url);
      const returnUrl = urlTree.queryParamMap.get('returnUrl');
      if (!returnUrl || typeof returnUrl !== 'string') return null;
      // Allow only internal relative paths; reject external / protocol-relative URLs.
      if (!returnUrl.startsWith('/') || returnUrl.startsWith('//')) return null;
      return returnUrl;
    } catch {
      return null;
    }
  }

  private identifyUserInAnalytics(user: User): void {
    if (!user?.id) return;
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    this._analyticsService.identify(user.id, {
      email: user.email,
      name: fullName || undefined,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.roleDisplayName || user.role,
      companyName: user.companyName
    });
  }

  captureLoginFailed(email?: string, reason?: string): void {
    this._analyticsService.capture(AnalyticsEvent.LoginFailed, {
      ...(email ? { email } : {}),
      ...(reason ? { reason } : {})
    });
  }

  captureSessionExpired(): void {
    this._analyticsService.capture(AnalyticsEvent.SessionExpired, {});
  }
}
